import React, { useState, useCallback, useEffect } from "react";
import { GetUsersOutput, User, AdminGetUsers } from "../../api/user";
import AsyncSelect from "react-select/async";
import { StylesConfig, ValueType } from "react-select";

export interface UserOption {
  label: string;
  value: string;
  user: User;
}

export type SelectedUserEvent = {
  id: string;
  user: UserOption;
};

interface UserSelectProps {
  findUsers?: ({
    filter,
  }: {
    filter?: string;
  }) => Promise<GetUsersOutput> | GetUsersOutput;
  onSelectUser?: (e: SelectedUserEvent) => Promise<void> | void;
  // offer an X to clear the selection, reported through onClear
  isClearable?: boolean;
  onClear?: () => void;
  // users left out of the choices, by ID
  excludeUserIDs?: string[];
  isDisabled?: boolean;
  value?: UserOption;
}

// MUI dialogs sit at z-index 1300
const menuStyles: StylesConfig = {
  menuPortal: (base) => ({ ...base, zIndex: 1301 }),
};

const defaultFindUsers = async ({
  filter,
}: {
  filter?: string;
}): Promise<GetUsersOutput> => {
  return AdminGetUsers({ filterUsernameLike: filter });
};

const UserSelect = ({
  findUsers = defaultFindUsers,
  onSelectUser,
  isClearable,
  onClear,
  excludeUserIDs,
  isDisabled,
  value,
}: UserSelectProps): JSX.Element => {
  const [selectedUser, setSelectedUser] = useState<ValueType<UserOption>>(null);

  useEffect(() => {
    setSelectedUser(value);
  }, [value]);

  const handleSelectedUserChanged = useCallback(
    (v: ValueType<UserOption>) => {
      setSelectedUser(v);
      // null when the selection is cleared
      const user = v as UserOption;
      if (user) {
        onSelectUser?.({ id: user.user.id, user });
      } else {
        onClear?.();
      }
    },
    [onSelectUser, onClear]
  );

  const loadOptions = useCallback(
    async (inputValue: string): Promise<UserOption[]> => {
      try {
        const r = await findUsers({ filter: inputValue });
        return (r.items || [])
          .filter((i) => !excludeUserIDs?.includes(i.id))
          .map((i) => {
            return { value: i.id, label: i.email, user: i };
          });
      } catch (e) {
        console.log(e);
        return [];
      }
    },
    [findUsers, excludeUserIDs]
  );

  return (
    <AsyncSelect
      // Async loads its default options once and caches what it loads, so a
      // change to the exclusions needs a fresh instance; the selection is ours
      // and survives
      key={(excludeUserIDs || []).join(",")}
      defaultOptions={true}
      cacheOptions
      placeholder="Enter user email..."
      onChange={handleSelectedUserChanged}
      loadOptions={loadOptions}
      noOptionsMessage={(): string => "No users found"}
      isDisabled={isDisabled}
      isClearable={isClearable}
      // null, not undefined, shows no selection once one has been cleared
      value={selectedUser || null}
      className="react-select"
      classNamePrefix="react-select"
      // the menu draws over whatever holds the picker rather than inside it:
      // inline, a dialog's scrolling content clipped it, and react-select
      // swallows wheel events over a list that cannot scroll, so the dialog
      // could not be scrolled from over the list either
      menuPortalTarget={document.body}
      styles={menuStyles}
      // opens upward when there is no room below
      menuPlacement="auto"
    />
  );
};

export default UserSelect;
